import React, { useState, useEffect } from 'react';
import api from '../../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import { getDefaultFinancialsDateRange } from '../../utils/financialsDateRange';
import { exportFinancialsListPdf } from '../../utils/financialsListPdf';
import FinancialsDateRangeFilter from '../../components/FinancialsDateRangeFilter/FinancialsDateRangeFilter';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import '../../styles/ListPage.css';

function formatMoney(value) {
    const num = parseFloat(value);
    if (Number.isNaN(num)) return '—';
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatQuantity(value) {
    const num = parseFloat(value);
    if (Number.isNaN(num)) return '—';
    return num.toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

function PurchaseList() {
    const navigate = useNavigate();
    const { isLoading, withLoading } = usePageLoading(true);
    const defaultRange = getDefaultFinancialsDateRange();
    const [fromDate, setFromDate] = useState(defaultRange.fromDate);
    const [toDate, setToDate] = useState(defaultRange.toDate);
    const [purchases, setPurchases] = useState([]);
    const [selectedPurchases, setSelectedPurchases] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredPurchases, setFilteredPurchases] = useState([]);

    useEffect(() => {
        withLoading(async () => {
            const res = await api.get('/purchases', {
                params: { fromDate, toDate }
            });
            setPurchases(res.data);
            setFilteredPurchases(res.data);
            setSelectedPurchases([]);
        }).catch((err) => {
            console.error('Error fetching purchases:', err);
            const message = err.response?.data?.Error || err.message || 'Error fetching purchases';
            alert(message);
        });
    }, [fromDate, toDate, withLoading]);

    useEffect(() => {
        const term = searchTerm.toLowerCase();
        const results = purchases.filter((entry) =>
            (entry.description || '').toLowerCase().includes(term) ||
            (entry.projectName || '').toLowerCase().includes(term) ||
            (entry.clientName || '').toLowerCase().includes(term)
        );
        setFilteredPurchases(results);
    }, [searchTerm, purchases]);

    const handleCheckboxChange = (purchaseId) => {
        setSelectedPurchases((prev) =>
            prev.includes(purchaseId) ? prev.filter((id) => id !== purchaseId) : [...prev, purchaseId]
        );
    };

    const handleSelectAll = (e) => {
        setSelectedPurchases(e.target.checked ? filteredPurchases.map((entry) => entry.id) : []);
    };

    const handleDeleteSelected = () => {
        if (!window.confirm('Are you sure you want to delete the selected purchases?')) return;
        Promise.all(selectedPurchases.map((purchaseId) => api.delete(`/purchases/${purchaseId}`)))
            .then(() => {
                setPurchases((prev) => prev.filter((entry) => !selectedPurchases.includes(entry.id)));
                setSelectedPurchases([]);
            })
            .catch(() => {
                alert('Error deleting purchases');
            });
    };

    const allSelected = filteredPurchases.length > 0 && selectedPurchases.length === filteredPurchases.length;

    const handleRangeChange = (nextFromDate, nextToDate) => {
        setFromDate(nextFromDate);
        setToDate(nextToDate);
    };

    const exportPurchasePdf = async () => {
        if (filteredPurchases.length === 0) {
            alert('No purchase entries to export');
            return;
        }

        const totalAmount = filteredPurchases.reduce(
            (sum, entry) => sum + parseFloat(entry.amount || 0),
            0
        );

        try {
            await exportFinancialsListPdf({
                pageTitle: 'Purchase',
                fileName: `purchase_${fromDate}_to_${toDate}.pdf`,
                fromDate,
                toDate,
                searchTerm,
                head: [['Date', 'Description', 'Quantity', 'Rate', 'Amount', 'Project']],
                body: filteredPurchases.map((entry) => [
                    formatDateDDMMYYYY(entry.date),
                    entry.description || '',
                    formatQuantity(entry.quantity),
                    formatMoney(entry.rate),
                    formatMoney(entry.amount),
                    entry.projectName || '—'
                ]),
                totalAmount: formatMoney(totalAmount),
                columnStyles: {
                    0: { cellWidth: 22 },
                    2: { halign: 'right' },
                    3: { halign: 'right' },
                    4: { halign: 'right' },
                    5: { cellWidth: 28 }
                }
            });
        } catch (error) {
            console.error('Error exporting purchase PDF:', error);
            alert('Error exporting purchase PDF');
        }
    };

    return (
        <PageLoadingArea isLoading={isLoading}>
            <div className="list-page">
                <div className="list-page-header">
                    <div className="list-page-title">
                        <h1>Purchase</h1>
                        <p className="list-page-subtitle">
                            {filteredPurchases.length} entr{filteredPurchases.length === 1 ? 'y' : 'ies'} · {formatDateDDMMYYYY(fromDate)} to {formatDateDDMMYYYY(toDate)}
                        </p>
                    </div>
                    <div className="list-header-actions">
                        <div className="list-search-wrap">
                            <input
                                type="text"
                                placeholder="Search description, project, or client"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="list-search-input"
                            />
                        </div>
                        <div className="list-button-group">
                            <button
                                type="button"
                                onClick={() => navigate('/financials')}
                                className="list-secondary-button"
                            >
                                Back to financials
                            </button>
                            <button
                                type="button"
                                onClick={exportPurchasePdf}
                                className="list-secondary-button"
                                disabled={filteredPurchases.length === 0}
                            >
                                Export PDF
                            </button>
                            <button type="button" onClick={() => navigate('/createPurchase')} className="list-add-button">
                                Add purchase
                            </button>
                            {selectedPurchases.length > 0 && (
                                <button type="button" onClick={handleDeleteSelected} className="list-delete-button">
                                    Delete ({selectedPurchases.length})
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <FinancialsDateRangeFilter
                    fromDate={fromDate}
                    toDate={toDate}
                    onRangeChange={handleRangeChange}
                />

                <div className="list-table-card">
                    <div className="list-table-scroll">
                        <table className="list-table">
                            <thead>
                                <tr>
                                    <th className="col-check">
                                        <input type="checkbox" checked={allSelected} onChange={handleSelectAll} aria-label="Select all" />
                                    </th>
                                    <th>Date</th>
                                    <th>Description</th>
                                    <th>Quantity</th>
                                    <th>Rate</th>
                                    <th>Amount</th>
                                    <th>Project</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPurchases.length === 0 ? (
                                    <tr className="list-empty-row">
                                        <td colSpan="7">
                                            <div className="list-empty-state">
                                                <h3>No purchase entries found</h3>
                                                <p>{searchTerm ? 'Try a different search term.' : 'Add your first purchase entry to get started.'}</p>
                                                {!searchTerm && (
                                                    <button
                                                        type="button"
                                                        className="list-add-button"
                                                        onClick={() => navigate('/createPurchase')}
                                                    >
                                                        Add purchase
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredPurchases.map((entry) => (
                                    <tr
                                        key={entry.id}
                                        className={`list-row${selectedPurchases.includes(entry.id) ? ' is-selected' : ''}`}
                                        onClick={(e) => {
                                            if (e.target.type !== 'checkbox') navigate(`/createPurchase/${entry.id}`);
                                        }}
                                    >
                                        <td className="col-check" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selectedPurchases.includes(entry.id)}
                                                onChange={() => handleCheckboxChange(entry.id)}
                                            />
                                        </td>
                                        <td>{formatDateDDMMYYYY(entry.date)}</td>
                                        <td><span className="list-primary-text">{entry.description}</span></td>
                                        <td className="col-num">{formatQuantity(entry.quantity)}</td>
                                        <td className="col-num">{formatMoney(entry.rate)}</td>
                                        <td className="col-num">{formatMoney(entry.amount)}</td>
                                        <td>{entry.projectName || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </PageLoadingArea>
    );
}

export default PurchaseList;
